/**
 * Phase A — Court Orders standalone module.
 *
 * Top-level page at /app/court-orders. Architecture: each parent
 * relationship (Alex ↔ Sam, Alex ↔ Parent 3, …) is a tab. Within
 * the active relationship the user sees its orders (active +
 * scheduled + historical) and the live compliance summary. Drilling
 * into an order shows its sectioned editor.
 *
 * This is the central screen that — in coming phases — will surface
 * everything governed by a Court Order: Expenses, Parenting
 * Schedule, Contact Visits, Maintenance and Insights. Today we ship
 * the relationship picker + order list + compliance summary + order
 * setup, so the foundation is in place.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Gavel, Users, ChevronRight, Plus, Pencil, CalendarOff, History } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CourtOrderCreateDialog } from "@/components/courtOrders/CourtOrderCreateDialog";
import { CourtOrderDetailPanel } from "@/components/courtOrders/CourtOrderDetailPanel";
import { orderSubjectLabel } from "@/components/courtOrders/courtOrderTypes";

// Iter 127 — Status → badge palette. Kept intentionally muted so
// the group headers themselves carry the eye's first pass; each
// order still shows its own status badge for quick scanning.
const STATUS_BADGE = {
  active: "border-[#A7D8DA] bg-[#D6F0EF] text-[#0F5658]",
  draft: "border-[#E6CFA2] bg-[#FBF0DC] text-[#6F4E1E]",
  scheduled: "border-[#E6CFA2] bg-[#FBF0DC] text-[#6F4E1E]",
  expired: "border-[#E1B8B3] bg-[#F3D9D6] text-[#7A2E24]",
  ended: "border-[#D9E1E3] bg-[#EEF3F4] text-[#6F828A]",
  superseded: "border-[#D9E1E3] bg-[#EEF3F4] text-[#6F828A]",
};

function statusLabel(s) {
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function splitLabel(o) {
  const sec = (o?.sections || []).find((s) => s.type === "expense_reimbursement");
  const stype = sec?.payload?.split_type || o?.split_type;
  if (!stype) return "—";
  if (stype === "custom") return "Custom";
  return stype.replace("_", " / ");
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function RelationshipTab({ rel, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 sm:px-4 py-2 rounded-sm border text-left text-sm transition-colors ${
        active
          ? "border-[#1F9FA4] bg-[#E0F2F2] text-[#0F5658]"
          : "border-[#D9E1E3] bg-white text-[#3F535A] hover:bg-[#F4FAFA]"
      }`}
      data-testid={`co-relationship-tab-${rel.id}`}
      aria-pressed={active}
    >
      <div className="flex items-center gap-1.5">
        <Users className="w-3.5 h-3.5" strokeWidth={1.7} />
        <span className="font-medium truncate max-w-[200px]">{rel.label}</span>
      </div>
      {rel.children?.length ? (
        <div className="text-[11px] text-[#6F828A] mt-0.5 truncate">
          {rel.children.map((c) => c.name).join(", ")}
        </div>
      ) : (
        <div className="text-[11px] text-[#6F828A] mt-0.5 italic">
          No children linked yet
        </div>
      )}
    </button>
  );
}

/**
 * Iter 127 — group wrapper. Sticky-ish label above a set of order
 * cards. Keeps the left rail compact even when a household has
 * many orders across statuses.
 */
function OrderGroup({ label, testId, children }) {
  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.12em] text-[#6F828A] font-medium pl-1">
        {label}
      </div>
      <div className="space-y-2">
        {children}
      </div>
    </div>
  );
}

function OrderCard({ order, isActive, selected, onSelect }) {
  const badgeCls = STATUS_BADGE[order.status] || STATUS_BADGE.superseded;
  const subject = orderSubjectLabel(order.subject, order.subject_other);
  const expiryDisplay = order.no_expiry
    ? "No expiry"
    : (order.effective_to ? fmtDate(order.effective_to) : "Ongoing");
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`text-left w-full border rounded-md p-3 sm:p-4 transition-colors ${
        selected
          ? "border-[#1F9FA4] bg-[#E0F2F2]"
          : "border-[#D9E1E3] bg-white hover:bg-[#F7FAFA]"
      }`}
      data-testid={`co-order-card-${order.id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
        <div className="flex items-center gap-1.5 min-w-0">
          <Gavel className="w-3.5 h-3.5 text-[#1F9FA4] flex-shrink-0" strokeWidth={1.7} />
          <span className="font-medium text-[#0B2E32] truncate">{order.name}</span>
        </div>
        <span
          className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border rounded-sm ${badgeCls}`}
          data-testid={`co-order-status-${order.id}`}
        >
          {isActive ? "Active" : statusLabel(order.status)}
        </span>
      </div>
      <div className="text-[11px] text-[#6F828A] mt-0.5 mb-1 font-medium">
        {subject}
      </div>
      {order.reference ? (
        <div className="text-[11px] text-[#6F828A] font-mono-data">
          Ref: {order.reference}
        </div>
      ) : null}
      <div className="text-[11px] text-[#6F828A] mt-1.5 flex items-center gap-3 flex-wrap font-mono-data">
        <span>{fmtDate(order.effective_from)} → {expiryDisplay}</span>
        <span>·</span>
        <span>{(order.sections || []).length} section{(order.sections || []).length === 1 ? "" : "s"}</span>
      </div>
      {order.review_date ? (
        <div className="text-[11px] text-[#6F4E1E] mt-1">
          Review: {fmtDate(order.review_date)}
        </div>
      ) : null}
      {order.supersedes_id ? (
        <div className="text-[11px] text-[#6F828A] mt-1 italic">
          Replaces earlier version
        </div>
      ) : null}
      {order.issuing_court ? (
        <div className="text-[11px] text-[#6F828A] mt-1 truncate">
          {order.issuing_court}
          {order.judge ? ` · ${order.judge}` : ""}
        </div>
      ) : null}
    </button>
  );
}

export default function CourtOrders() {
  const [relationships, setRelationships] = useState([]);
  const [activeRelId, setActiveRelId] = useState("");
  const [orders, setOrders] = useState([]);
  const [activeByRel, setActiveByRel] = useState({});
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  const loadRels = useCallback(async () => {
    const { data } = await api.get("/relationships");
    setRelationships(data);
    if (data.length > 0 && !activeRelId) {
      setActiveRelId(data[0].id);
    }
  }, [activeRelId]);

  const loadOrders = useCallback(async () => {
    if (!activeRelId) {
      setOrders([]);
      setActiveByRel({});
      return;
    }
    const { data } = await api.get("/court-orders", {
      params: { relationship_id: activeRelId },
    });
    setOrders(data.orders || []);
    setActiveByRel(data.active_by_relationship || {});
    // Auto-select the active order, else the most recent
    const activeId = data.active_by_relationship?.[activeRelId];
    if (activeId) setSelectedOrderId(activeId);
    else if (data.orders?.length) setSelectedOrderId(data.orders[0].id);
    else setSelectedOrderId("");
  }, [activeRelId]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await loadRels();
      } finally {
        setLoading(false);
      }
    })();
  }, [loadRels]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const activeRel = useMemo(
    () => relationships.find((r) => r.id === activeRelId) || null,
    [relationships, activeRelId],
  );
  const activeOrderId = activeByRel[activeRelId];
  const selectedOrder = orders.find((o) => o.id === selectedOrderId) || null;

  // Iter 127 — Group orders by status buckets. Upcoming = draft or
  // scheduled with an effective_from in the future. Active =
  // currently in force. Expired/ended = historical (but were never
  // replaced). Superseded = explicitly replaced by a same-subject
  // successor.
  const todayIso = new Date().toISOString().slice(0, 10);
  const grouped = useMemo(() => {
    const buckets = { active: [], upcoming: [], expired_ended: [], superseded: [] };
    for (const o of orders) {
      const status = o.status;
      if (status === "superseded") {
        buckets.superseded.push(o);
      } else if (status === "expired" || status === "ended") {
        buckets.expired_ended.push(o);
      } else if (status === "active") {
        buckets.active.push(o);
      } else if ((status === "draft" || status === "scheduled") && o.effective_from > todayIso) {
        buckets.upcoming.push(o);
      } else if (status === "draft" || status === "scheduled") {
        // Draft/scheduled whose effective_from has already passed —
        // surface alongside active so the parent notices they need
        // to activate it.
        buckets.active.push(o);
      } else {
        buckets.expired_ended.push(o);
      }
    }
    return buckets;
  }, [orders, todayIso]);
  const visibleOrders = useMemo(() => {
    if (showHistory) return orders;
    // Default view: hide superseded + expired/ended.
    return [...grouped.active, ...grouped.upcoming];
  }, [orders, grouped, showHistory]);

  const handleOrderSaved = async () => {
    await loadOrders();
    toast.success("Order saved");
  };

  if (loading) {
    return (
      <div
        className="border border-[#D9E1E3] bg-white rounded-md p-6 text-sm text-[#6F828A]"
        data-testid="court-orders-loading"
      >
        Loading court orders…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="court-orders-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.12em] text-[#6F828A]">
            Governing rules
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium mt-1 inline-flex items-center gap-3 flex-wrap">
            <Gavel className="w-6 h-6 text-[#1F9FA4]" strokeWidth={1.6} />
            Court Orders
          </h1>
          <p className="text-sm text-[#6F828A] mt-1 max-w-prose">
            Capture every court order or parenting agreement that governs
            this household. Each order belongs to a specific parent
            relationship and can be scoped to all children or a subset.
            Compliance is measured against the order that was in effect
            when each expense was incurred — historical records remain
            reproducible even when an order is later amended.
          </p>
        </div>
        {activeRel ? (
          <Button
            onClick={() => setCreateOpen(true)}
            className="bg-[#1F9FA4] hover:bg-[#178085] rounded-sm h-10 px-4"
            data-testid="co-new-order-button"
          >
            <Plus className="w-4 h-4 mr-2" /> New order
          </Button>
        ) : null}
      </div>

      {/* Relationship tabs */}
      {relationships.length === 0 ? (
        <div
          className="border border-[#D9E1E3] bg-white rounded-md p-6 text-sm text-[#3F535A]"
          data-testid="co-no-relationships"
        >
          <Users className="w-6 h-6 text-[#1F9FA4] mb-2" strokeWidth={1.5} />
          <p>
            No parent relationships found yet. Once a co-parent is linked to
            this household, this page will surface them here so you can
            create orders specific to each pair.
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="co-relationship-tabs">
          <div className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">
            Relationships
          </div>
          <div className="flex items-stretch gap-2 flex-wrap">
            {relationships.map((r) => (
              <RelationshipTab
                key={r.id}
                rel={r}
                active={r.id === activeRelId}
                onClick={() => {
                  setActiveRelId(r.id);
                  setSelectedOrderId("");
                  setShowHistory(false);
                }}
              />
            ))}
          </div>
        </div>
      )}

      {activeRel ? (
        <div
          className="grid grid-cols-1 lg:grid-cols-[minmax(0,360px)_minmax(0,1fr)] gap-4"
          data-testid="co-layout"
        >
          {/* Left rail: orders for this relationship */}
          <div className="space-y-2 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">
                Orders
              </div>
              <button
                type="button"
                onClick={() => setShowHistory((v) => !v)}
                className="text-[11px] text-[#6F828A] hover:text-[#2E3A3F] inline-flex items-center gap-1"
                data-testid="co-history-toggle"
              >
                <History className="w-3 h-3" strokeWidth={1.7} />
                {showHistory ? "Hide history" : "Show history"}
              </button>
            </div>
            {visibleOrders.length === 0 ? (
              <div
                className="border border-dashed border-[#D9E1E3] rounded-md p-5 text-sm text-[#6F828A] text-center"
                data-testid="co-empty-orders"
              >
                <Gavel
                  className="w-6 h-6 mx-auto mb-2 text-[#1F9FA4]"
                  strokeWidth={1.5}
                />
                <p className="mb-3">
                  No active order for {activeRel.label}.
                </p>
                <Button
                  onClick={() => setCreateOpen(true)}
                  className="bg-[#1F9FA4] hover:bg-[#178085] rounded-sm"
                  size="sm"
                  data-testid="co-empty-add-order"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add the first order
                </Button>
              </div>
            ) : (
              <div className="space-y-4" data-testid="co-orders-list">
                {/* Iter 127 — Grouped listing so the user can tell at
                    a glance which orders are currently in force. */}
                {grouped.active.length > 0 && (
                  <OrderGroup label={`Active (${grouped.active.length})`} testId="co-group-active">
                    {grouped.active.map((o) => (
                      <OrderCard
                        key={o.id}
                        order={o}
                        isActive={o.id === activeOrderId || o.status === "active"}
                        selected={o.id === selectedOrderId}
                        onSelect={() => setSelectedOrderId(o.id)}
                      />
                    ))}
                  </OrderGroup>
                )}
                {grouped.upcoming.length > 0 && (
                  <OrderGroup label={`Upcoming (${grouped.upcoming.length})`} testId="co-group-upcoming">
                    {grouped.upcoming.map((o) => (
                      <OrderCard
                        key={o.id}
                        order={o}
                        isActive={false}
                        selected={o.id === selectedOrderId}
                        onSelect={() => setSelectedOrderId(o.id)}
                      />
                    ))}
                  </OrderGroup>
                )}
                {showHistory && grouped.expired_ended.length > 0 && (
                  <OrderGroup label={`Expired / ended (${grouped.expired_ended.length})`} testId="co-group-expired">
                    {grouped.expired_ended.map((o) => (
                      <OrderCard
                        key={o.id}
                        order={o}
                        isActive={false}
                        selected={o.id === selectedOrderId}
                        onSelect={() => setSelectedOrderId(o.id)}
                      />
                    ))}
                  </OrderGroup>
                )}
                {showHistory && grouped.superseded.length > 0 && (
                  <OrderGroup label={`Superseded (${grouped.superseded.length})`} testId="co-group-superseded">
                    {grouped.superseded.map((o) => (
                      <OrderCard
                        key={o.id}
                        order={o}
                        isActive={false}
                        selected={o.id === selectedOrderId}
                        onSelect={() => setSelectedOrderId(o.id)}
                      />
                    ))}
                  </OrderGroup>
                )}
              </div>
            )}
          </div>

          {/* Right pane: order detail + compliance for this relationship */}
          <div className="space-y-4 min-w-0">
            {selectedOrder ? (
              <CourtOrderDetailPanel
                order={selectedOrder}
                relationship={activeRel}
                isActive={selectedOrder.id === activeOrderId}
                onChanged={loadOrders}
                onJumpToOrder={(oid) => {
                  setShowHistory(true);
                  setSelectedOrderId(oid);
                }}
              />
            ) : (
              <div
                className="border border-dashed border-[#D9E1E3] rounded-md p-8 text-sm text-[#6F828A] text-center"
                data-testid="co-no-selection"
              >
                Select an order on the left to view its sections, compliance
                summary, and history.
              </div>
            )}

            {/* Iter 127.b — Compliance summary REMOVED from the
                Court Orders page. Court Orders define the rules;
                Expenses calculates + displays compliance. Keeping
                one source of truth so users don't see duplicate
                numbers here. A compact link points them to the
                dedicated dashboard. */}
            {selectedOrder && (selectedOrder.status === "active") ? (
              <div
                className="flex items-center justify-between gap-3 flex-wrap border border-[#D9E1E3] bg-white rounded-md p-3"
                data-testid="co-detail-compliance-link"
              >
                <div>
                  <div className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium mb-0.5">
                    Compliance
                  </div>
                  <div className="text-sm text-[#0B2E32]">
                    This order is currently governing <strong>Expenses</strong> compliance.
                  </div>
                </div>
                <Link
                  to="/app/expenses?tab=compliance"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-sm border border-[#A7D8DA] text-[#0F5658] hover:bg-[#E0F2F2] transition-colors"
                  data-testid="co-detail-compliance-link-btn"
                >
                  View compliance dashboard
                  <ChevronRight className="w-3.5 h-3.5" strokeWidth={2} />
                </Link>
              </div>
            ) : null}

            {/* iter 126.cb-relationship — Export a Court Bundle
                scoped to this exact relationship. Deep-links the
                Court Bundle page with `?relationship_id=…` so the
                selector pre-selects the pair and the child list
                collapses to just their children. */}
            <div className="flex items-center justify-between gap-3 flex-wrap border border-[#D9E1E3] bg-[#F7FAFA] rounded-md p-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium mb-0.5">
                  Court Bundle
                </div>
                <div className="text-sm text-[#0B2E32]">
                  Preview and export a bundle scoped to <strong>{activeRel.label}</strong>.
                </div>
              </div>
              <Link
                to={`/app/court-bundle?relationship_id=${activeRel.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-sm bg-[#1F9FA4] hover:bg-[#178085] text-white transition-colors"
                data-testid={`co-export-bundle-${activeRel.id}`}
              >
                Preview bundle
                <ChevronRight className="w-3.5 h-3.5" strokeWidth={2} />
              </Link>
            </div>

            <div className="border border-dashed border-[#D9E1E3] bg-white rounded-md p-4 text-[12px] text-[#6F828A]">
              <div className="flex items-center gap-2 mb-1.5">
                <ChevronRight className="w-3 h-3" strokeWidth={1.7} />
                <span className="uppercase tracking-[0.12em] font-medium">
                  What this order governs
                </span>
              </div>
              <ul className="space-y-0.5 list-disc pl-5">
                <li>
                  <strong className="text-[#3F535A]">Expenses</strong> — the
                  reimbursement split + window applied to each new expense.
                </li>
                <li>
                  <strong className="text-[#3F535A]">Compliance</strong> —
                  the dashboard above tracks settled / pending / past-window
                  per parent.
                </li>
                <li className="opacity-70">
                  Parenting Schedule, Contact Visits, Maintenance and Court
                  Bundle will wire in next.{" "}
                  <Link
                    to="/app/expenses"
                    className="text-[#1F9FA4] hover:text-[#178085] underline"
                  >
                    See expenses →
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      ) : null}

      <CourtOrderCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        relationship={activeRel}
        onCreated={handleOrderSaved}
      />
    </div>
  );
}
